import CTAButton from "@components/CTAButton";
import Icon from "@components/Icon";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES, MARKS } from "@contentful/rich-text-types";
import {
	colours,
	device,
	filterUrlPath,
	getColours,
	isAnchorOrDocumentLink,
	isEmptyContentfulEntry,
	isFelixMobileUrl,
	richTextHelper,
} from "@helpers";
import { mixpanelTrack } from "@helpers/mixpanel.helper";
import { useLightboxContext, useQrModalContext } from "@hooks";
import type { IModulePlanFields } from "@types-contentful";
import Link from "next/link";
import type React from "react";
import styled from "styled-components";

const Price = styled.div<{ discounted: boolean }>`
  .originalPrice {
    text-decoration: ${(props) => (props.discounted ? "line-through" : "none")};
	${(props) => (props.discounted ? "font-size: 28px" : "")}
  }
  .discountedPrice {
    // margin-left: 10px;
    color: ${colours.MIDNIGHT_BLUE};
	font-weight: 700;
  }
`;
interface IModulePlanProps extends IModulePlanFields {
	moduleId?: string;
}

const Container = styled.div`
  width: 100%;
`;

const HeadPanel = styled.div<{
	productName: string | undefined;
	backgroundColor:
		| "Midnight Blue"
		| "White"
		| "Peach"
		| "Pale Peach"
		| "Watermelon"
		| "Mint Green"
		| "Green"
		| undefined;
}>`
  margin: 0 20px;

  @media (min-width: ${device.tabletL}) {
		${({ productName }) => productName && "margin-left: 0 !important;"} 
	}

  display: flex;
  flex-direction: column;
  vertical-align: bottom;
  background-color: ${({ backgroundColor }) => getColours(backgroundColor)};
  & > * {
    &:first-child {
      margin-right: 10px;
    }
  }



  
`;

const NoLineBreakDiv = styled.div`
  margin: 16px 0 8px;
`;

const Subheadline = styled.div<{ productName: string | undefined }>`
  	margin: 0 20px;
	margin-bottom: 17px;
	@media (max-width: ${device.tabletL}) {
		${({ productName }) => !productName && "margin-bottom: 41px;"} 
	}

	h2 {
		${({ productName }) => productName && "margin-bottom: 17px;"} 
	}
	h2:last-of-type {
		margin-bottom: 0 !important;
	}

	display: flex;
	flex-direction: column;
	#month {
		display: flex;
		align-items: baseline;
	}

	@media (min-width: ${device.tabletL}) {
    flex-direction: row;
    justify-content: space-between;
    margin: 0;
    margin-right: 30px;
  }

`;

const PriceBody = styled.div`
	@media (min-width: ${device.tabletL}) {
    margin-bottom: 17px;
  }

`;

const PlanPanelWrapper = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
`;

const PlanPanel = styled.div<{
	backgroundColor:
		| "Midnight Blue"
		| "White"
		| "Peach"
		| "Pale Peach"
		| "Watermelon"
		| "Mint Green"
		| "Green"
		| undefined;
}>`
  background-color: ${({ backgroundColor }) => getColours(backgroundColor)};
  border-radius: 10px;
  box-shadow: 0 4px 15px 0 rgba(18, 18, 82, 0.2);
  color: ${colours.MIDNIGHT_BLUE};
  margin-left: 20px;
  margin-right: 20px;
  padding: 26px 10px;
  width: 100%;
  & h2 {
    padding-bottom: 0px;
  }

  @media (min-width: ${device.tablet}) and (max-width: ${device.tabletL}) {
    margin-left: 64px;
    margin-right: 64px;
    padding: 34px 30px;
  }

  @media (min-width: ${device.laptop}) and (max-width: ${device.desktopS}) {
    margin-left: 166px;
    margin-right: 166px;
    padding: 49px;
  }

  @media (min-width: ${device.desktopS}) and (max-width: ${device.desktop}) {
    margin-left: 241px;
    margin-right: 241px;
    padding: 55px 73px;
    border-radius: 20px;
  }

  @media (min-width: ${device.desktop}) {
    margin-left: 561px;
    margin-right: 561px;
    padding: 55px 73px;
    border-radius: 20px;
  }
`;

const Row = styled.div<{ NoPlanItemHeadline: boolean | undefined }>`
  display: flex;
  flex-direction: row;
  flex: 1;
  object-fit: contain;
  align-items: ${({ NoPlanItemHeadline }) =>
		NoPlanItemHeadline ? "center" : "flex-start"};

  :last-child {
    > span {
      margin-bottom: 0px;
    }
  }
`;

const RowText = styled.span`
  margin: 0px 0px 20px 10px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  margin-top: 10px;
`;

const Headline = styled.div`
  font-family: var(--euclid-light);
  margin-bottom: 12px;
  vertical-align: bottom;
`;

const HeadlineBold = styled.span`
  font-family: var(--euclid-medium);
  margin-bottom: 5px;
  font-weight: 700;
  @media (min-width: ${device.tablet}) {
    margin-bottom: 8px;
  }

  @media (min-width: ${device.desktopS}) {
    margin-bottom: 12px;
  }
`;

const HeadlineHyperLink = styled.a`
  font-family: EuclidCircularABold !important;
  margin-bottom: 5px;
  text-decoration: underline;

  @media (min-width: ${device.tablet}) {
    margin-bottom: 8px;
  }

  @media (min-width: ${device.desktopS}) {
    margin-bottom: 12px;
  }
`;

const SmallPrint = styled.div`
  margin: 0 auto;
  padding-top: 8px;
  width: 307px;
  p,
  b {
    font-size: 14px;
  }
  @media (min-width: 1165px) {
    width: 928px;
    text-align: center;
    padding-top: 32px;
  }
`;

const Button = styled.button`
  background-color: ${colours.WATERMELON};
`;

const RichTextParagraph = styled.p`
  margin-top: 0%;
`;

const RichTextH2 = styled.h2`
  margin-bottom: 24px;
`;

const RichTextOl = styled.ol`
  margin-top: 2%;
`;

const RichTextUl = styled.ul`
  margin-top: 2%;
`;

const RichTextLI = styled.li`
  margin-bottom: 1%;
  margin-top: 1%;
`;

const HR = styled.hr<{ productName: string | undefined }>`
  	margin: 4px 0px ${({ productName }) => (productName ? "24px" : "41px")} 0px;
	@media (max-width: ${device.tabletL}) {
		margin: 4px 0px 17px 0px;
	}
`;

const iconSizes = {
	iconMobile: 22,
	iconTablet: 22,
	iconLaptop: 22,
	iconDesktop: 28,
	iconDesktopLarge: 33,
};

const iconStyles = {
	"border-radius": "50%",
};
const PlanModule: React.FC<IModulePlanProps> = ({
	moduleId,
	cardBackground,
	ctaButtonLabel,
	ctaButtonLink,
	ctaButtonQrModalLabel,
	headlineBackground,
	mainHeadline,
	smallPrint,
	discountedPrice,
	copyUnderDiscountedPrice,
	planPrice,
	textAbovePlanItems,
	textBelowPlanItems,
	addLinebreak,
	ctaButtonQrModal,
	name,
	planItems,
	secondMainHeadline,
	productName,
	subHeadline,
}) => {
	// @ts-ignore
	const { setlightboxContext } = useLightboxContext();
	// @ts-ignore
	const { setQrModalId } = useQrModalContext();

	const qrModalClick = (e) => {
		e.preventDefault();
		setQrModalId(ctaButtonQrModal);
	};
	const headlineOptions = {
		renderMark: {
			[MARKS.BOLD]: (text) => <HeadlineBold>{text}</HeadlineBold>,
		},
		renderNode: {
			[BLOCKS.PARAGRAPH]: (_, children) => {
				return <Headline>{children}</Headline>;
			},
			[INLINES.ENTRY_HYPERLINK]: (node, children) => {
				const isLightBox =
					node.data?.target?.sys?.contentType?.sys?.contentful_id &&
					node.data.target.sys.contentType.sys.contentful_id ===
						"moduleLightBox";
				if (isLightBox) {
					const dataFields = node?.data?.target?.fields;
					const lightboxHeadline = dataFields?.headline?.["en-US"];
					const lightboxMainText = dataFields?.mainText?.["en-US"];
					const onLinkClick = (e) => {
						e.preventDefault();

						setlightboxContext({
							headline: lightboxHeadline,
							mainText: lightboxMainText,
						});
					};
					return (
						<HeadlineHyperLink
							data-gtm-cta-position="Plan module"
							data-gtm-cta-text={children}
							onClick={onLinkClick}
						>
							{children}
						</HeadlineHyperLink>
					);
				}
				const pageLink = node.data?.target?.relativeUrl;
				return <Link href={pageLink}>{children}</Link>;
			},
		},
	};

	const panelTextOptions = {
		renderNode: {
			[BLOCKS.HEADING_2]: (_, children) => <RichTextH2>{children}</RichTextH2>,
			[BLOCKS.PARAGRAPH]: (_, children) => <p>{children}</p>,
			[BLOCKS.OL_LIST]: (_, children) => (
				<RichTextParagraph>
					<RichTextOl>{children}</RichTextOl>
				</RichTextParagraph>
			),
			[BLOCKS.UL_LIST]: (_, children) => (
				<RichTextParagraph>
					<RichTextUl>{children}</RichTextUl>
				</RichTextParagraph>
			),
			[BLOCKS.LIST_ITEM]: (node, _) => {
				// hack https://github.com/contentful/rich-text/issues/126
				const UnTaggedChildren = documentToReactComponents(node, {
					renderNode: {
						[BLOCKS.PARAGRAPH]: (_, children1) => children1,
						[BLOCKS.LIST_ITEM]: (_, children1) => children1,
					},
				});

				return <RichTextLI>{UnTaggedChildren}</RichTextLI>;
			},
		},
	};

	return (
		<Container>
			<PlanPanelWrapper>
				<PlanPanel backgroundColor={cardBackground}>
					{!isEmptyContentfulEntry(mainHeadline) ? (
						<>
							<HeadPanel
								productName={productName}
								backgroundColor={headlineBackground}
							>
								<h2>{mainHeadline}</h2>
								{secondMainHeadline && <h2>{secondMainHeadline}</h2>}
							</HeadPanel>
							{(!isEmptyContentfulEntry(planPrice) ||
								!isEmptyContentfulEntry(subHeadline)) && (
								<>
									{addLinebreak ? (
										<HR productName={productName} />
									) : (
										<NoLineBreakDiv />
									)}
									<Subheadline productName={productName}>
										<h2>{productName}</h2>
										{/* {!planPrice && <h2>{subHeadline}</h2>} */}

										{discountedPrice && (
											<PriceBody>
												<Price id="month" discounted={Boolean(discountedPrice)}>
													{/* <h2
														className="originalPrice"
														data-testid="originalPrice"
													>
														${planPrice}
													</h2> */}
													{discountedPrice && (
														<h2
															className="discountedPrice"
															data-testid={`discountedPrice-${name?.toLowerCase().includes("unlimited") ? "Unlimited" : name?.includes("50") ? "50GB" : name?.includes("25") ? "25GB" : name}`}
														>
															${discountedPrice}
														</h2>
													)}
													<span style={{ fontSize: "18px" }}>
														{discountedPrice ? "/month" : ""}
													</span>
												</Price>
												{discountedPrice && (
													<span
														data-testid={`copyUnderDiscountedPrice-${name?.toLowerCase().includes("unlimited") ? "Unlimited" : name?.includes("50") ? "50GB" : name?.includes("25") ? "25GB" : name}`}
													>
														{copyUnderDiscountedPrice
															? copyUnderDiscountedPrice 
															: ""}
													</span>
												)}
											</PriceBody>
										)}
									</Subheadline>
								</>
							)}
						</>
					) : null}
					{!isEmptyContentfulEntry(textAbovePlanItems) &&
						richTextHelper(textAbovePlanItems, panelTextOptions)}

					{planItems?.map((item, index) => (
						<Row
							key={`${item.fields.planItemHeadline}+${index}`}
							NoPlanItemHeadline={isEmptyContentfulEntry(
								item.fields.planItemHeadline,
							)}
						>
							<div>
								<Icon
									iconName={item.fields.planItemIconName}
									iconSizes={iconSizes}
									backgroundColor="#fff"
									styles={iconStyles}
									alt={item.fields.planItemIconName}
								/>
							</div>
							<RowText>
								{!isEmptyContentfulEntry(item.fields.planItemHeadline) &&
									richTextHelper(item.fields.planItemHeadline, headlineOptions)}
								{!isEmptyContentfulEntry(item.fields.planItemText) &&
									richTextHelper(item.fields.planItemText)}
							</RowText>
						</Row>
					))}
					{!isEmptyContentfulEntry(textBelowPlanItems) && (
						<div>{richTextHelper(textBelowPlanItems, panelTextOptions)}</div>
					)}
					{!isEmptyContentfulEntry(ctaButtonLabel) && (
						<ButtonWrapper>
							<CTAButton
								url={ctaButtonLink}
								gtmCtaPosition={name}
								gtmCtaText={ctaButtonLabel}
							>
								{ctaButtonLabel}
							</CTAButton>
						</ButtonWrapper>
					)}
					{!isEmptyContentfulEntry(ctaButtonQrModalLabel) &&
						ctaButtonQrModal && (
							<ButtonWrapper>
								<Button
									onClick={qrModalClick}
									data-gtm-cta-position={name}
									data-gtm-cta-text={ctaButtonQrModalLabel}
									id={`cta_qr_modal_${moduleId}`}
								>
									{ctaButtonQrModalLabel}
								</Button>
							</ButtonWrapper>
						)}
				</PlanPanel>
			</PlanPanelWrapper>
			{!isEmptyContentfulEntry(smallPrint) && (
				<SmallPrint data-testid={`planModuleSmallPrint-${name?.toLowerCase().includes("unlimited") ? "Unlimited" : name?.includes("50") ? "50GB" : name?.includes("25") ? "25GB" : name}`}>{richTextHelper(smallPrint)}</SmallPrint>
			)}
		</Container>
	);
};

export default PlanModule;
